import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import NotificationCenter from "@/components/NotificationCenter";
import {
  LayoutDashboard, Package, Boxes, History, ShoppingCart, Receipt,
  Users, Wallet, Truck, FileBarChart, FileText, Settings as SettingsIcon,
  LogOut, Menu, X, BanknoteArrowDown, HandCoins, TrendingUp, Landmark,
} from "lucide-react";

const MGR = ["admin", "manager"];
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { section: "Inventory" },
  { to: "/inventory", label: "Products", icon: Package, end: true },
  { to: "/inventory/adjust", label: "Stock Adjustments", icon: Boxes, roles: MGR },
  { to: "/inventory/history", label: "Stock History", icon: History },
  { section: "Sales" },
  { to: "/pos", label: "New Sale (POS)", icon: ShoppingCart },
  { to: "/sales", label: "Sales History", icon: Receipt },
  { section: "Clients" },
  { to: "/clients", label: "Client List", icon: Users },
  { to: "/credit-ledger", label: "Credit Ledger", icon: Wallet },
  { section: "Suppliers", roles: MGR },
  { to: "/suppliers", label: "Supplier List", icon: Truck, roles: MGR },
  { to: "/purchases", label: "Purchase Orders", icon: FileText, roles: MGR },
  { to: "/payables", label: "Payables Ledger", icon: HandCoins, roles: MGR },
  { section: "Finance", roles: MGR },
  { to: "/expenses", label: "Expenses", icon: BanknoteArrowDown, roles: MGR },
  { to: "/profit", label: "Profit & Growth", icon: TrendingUp, roles: MGR },
  { to: "/financials", label: "Cash & Capital", icon: Landmark, roles: MGR },
  { to: "/reports", label: "Reports", icon: FileBarChart, roles: MGR },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: MGR, end: true },
];

// Keep items the role may see, then drop section headers left with no children.
function visibleNavFor(role) {
  const allowed = NAV.filter((i) => !i.roles || i.roles.includes(role));
  return allowed.filter((item, idx) => {
    if (!item.section) return true;
    const rest = allowed.slice(idx + 1);
    const nextSection = rest.findIndex((x) => x.section);
    const children = nextSection === -1 ? rest : rest.slice(0, nextSection);
    return children.length > 0;
  });
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: shop } = useQuery({
    queryKey: ["shop-settings"],
    queryFn: () => api.get("/settings/shop").then((r) => r.data),
  });

  return (
    <div className="min-h-screen flex bg-agri-bg">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed lg:sticky lg:top-0 z-40 inset-y-0 left-0 w-60 h-screen bg-agri-forest text-[#f7f5f0] flex flex-col leaf-pattern transform transition-transform
          ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Brand */}
        <div className="h-16 shrink-0 px-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img src={`${process.env.PUBLIC_URL}/logo.jpeg`} alt="ZaaM Agro Services" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5 shadow-sm shrink-0" />
            <div className="leading-tight">
              <div className="font-heading text-[15px]">ZaaM <span className="text-agri-amber">Agro</span></div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-white/50">Services</div>
            </div>
          </div>
          <button className="lg:hidden text-white/70 hover:text-white" onClick={() => setOpen(false)} data-testid="sidebar-close-btn">
            <X size={19} />
          </button>
        </div>

        {/* Nav (only this scrolls) */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-2.5 py-2.5 space-y-0.5">
          {visibleNavFor(user?.role).map((item, i) =>
            item.section ? (
              <div key={i} className="px-2.5 pt-3.5 pb-1 text-[10px] uppercase tracking-wider text-white/35 font-semibold first:pt-1">
                {item.section}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
                    isActive
                      ? "bg-white/10 text-white font-medium"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-r-full bg-agri-amber transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
                    <item.icon size={16} className="shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>

        {/* User (pinned) */}
        <div className="shrink-0 p-3 border-t border-white/10 bg-agri-forest-dark/40">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-agri-amber/20 grid place-items-center text-agri-amber text-sm font-semibold shrink-0">
              {user?.name?.[0] ?? "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-white/50 capitalize">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 px-4 lg:px-8 bg-white border-b border-[rgba(28,28,30,0.06)] flex items-center justify-between sticky top-0 z-30">
          <button
            data-testid="sidebar-open-btn"
            className="lg:hidden p-2 -ml-2"
            onClick={() => setOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="font-heading text-xl text-agri-ink hidden lg:block">
            Welcome back, <span className="text-agri-forest">{shop?.name || "AgroManager Pro"}</span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationCenter />
            <div className="text-xs text-agri-ink-2 hidden sm:block">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />}
    </div>
  );
}
